
import { americanToDecimal, decimalToAmerican } from "@/utils/calculators/odds-utils";
import {
  getLogoCode,
  normalizeSportsbookId,
  sportsbookIdToDisplay,
} from "@/lib/sportsbook-registry";
import { getSportsbookLogoPath } from "@/lib/sportsbook-logo";
import { sportsbookLogoUrl } from "@/lib/sportsbook-logo-url";
import { toBetLinkSet } from "@/lib/bet-links";
import type {
  OddsHistoryOpposite,
  OddsHistoryPeerBook,
  OddsHistoryQuoteCapacity,
  OddsHistoryQuoteContext,
} from "@/components/odds/odds-history-selection";


export interface OrderBookLevel {
  odds: number;
  liquidity: number;
  actual_money_at_risk?: number;
}

export interface BookData {
  selection_id?: string;
  odds_decimal: number;
  odds_american: number;
  liquidity: number;
  actual_money_at_risk: number;
  order_book?: OrderBookLevel[];
  total_liquidity?: number;
  bet_link?: string;
  stake_required?: number;
  fee_adjusted?: boolean;
}

export interface ListPeer {
  book: string;
  odds_american: number;
  limit?: number;
  bet_link?: string;
  edge_percent?: number;
  fee_adjusted?: boolean;
}

export interface PeerOdds extends ListPeer {
  selection_id?: string;
  odds_decimal: number;
  opp_odds_american?: number;
  opp_odds_decimal?: number;
}

export interface SideData {
  side: "OVER" | "UNDER" | "HOME" | "AWAY" | "DRAW" | "YES" | "NO";
  line: number;
  books: Record<string, BookData>;
  peers?: PeerOdds[];
}

export interface APIMarket {
  id?: string;
  event_id: string;
  event: string;
  event_start: string;
  home_team: string;
  away_team: string;
  league: string;
  sport: string;
  entity_type: string;
  entity_name: string;
  market: string;
  market_type: string;
  line: number;
  market_arity?: number;
  total_liquidity: number;
  sides?: SideData[];
  side_a: SideData | null;
  side_b: SideData | null;
  updated_at: string;
  image_url?: string;
  home_logo_url?: string;
  away_logo_url?: string;
}

export interface APIOpportunity {
  market_data: APIMarket;
  recommended_side: SideData;
  opposite_side: SideData;
  best_book: string;
  best_odds: number;
  fair_odds: number;
  edge_percent?: number;
  edge_supporting_liquidity: number;
  same_side_conviction_liquidity: number;
  whale_volume: number;
  whale_volume_mode: string;
  fair_outcome_share?: number;
}

export interface Side<P extends ListPeer = ListPeer> {
  name: string;
  line: number;
  side: "home" | "away" | "over" | "under" | "draw" | "yes" | "no";
  total_liquidity: number;
  actual_money_at_risk?: number;
  pinnacle_limit: number;
  books: Record<string, BookData>;
  peers: P[];
  best_odds: number;
  best_book: string;
  best_bet_link?: string;
  edge_percent?: number;
  demand: number;
}

export interface MarketGroup<P extends ListPeer = ListPeer> {
  market: string;
  line: number;
  sides: Side<P>[];
  total_liquidity?: number;
  pinnacle_limit: number;
  edge_supporting_liquidity: number;
  same_side_conviction_liquidity: number;
  market_arity?: number;
  fair_outcome_share?: number;
  third_side?: Side<P>;
}

export function getFairOutcomeShare(market: Pick<MarketGroup, "fair_outcome_share">): number {
  const share = market.fair_outcome_share;
  if (typeof share === "number" && share > 0 && share <= 1) {
    return share;
  }
  return 1;
}

export interface MarketOpportunity<P extends ListPeer = ListPeer> {
  id: string;
  event_id: string;
  event: string;
  event_start: string;
  home_team: string;
  away_team: string;
  league: string;
  sport: string;
  market_type?: string;
  updated_at: string;
  market: MarketGroup<P>;
  entity_type?: string;
  entity_name?: string;
  image_url?: string;
  home_logo_url?: string;
  away_logo_url?: string;
}

export type PlayMedia =
  | { kind: "player" | "team"; name: string; url: string }
  | { kind: "matchup"; away: string; home: string; awayLogoUrl: string | null; homeLogoUrl: string | null };

type TeamRowFields = Pick<MarketOpportunity, "event" | "home_team" | "away_team" | "entity_name" | "image_url" | "home_logo_url" | "away_logo_url"> & { id?: string };

const teamKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, "");

export function getTeamRowName(row: TeamRowFields): string | undefined {
  if (row.entity_name && row.entity_name !== row.event) return row.entity_name;
  if (row.image_url && row.image_url === row.home_logo_url) return row.home_team;
  if (row.image_url && row.image_url === row.away_logo_url) return row.away_team;
  const slug = row.id?.split("|")[3];
  if (!slug) return undefined;
  return [row.home_team, row.away_team].find((team) => team && teamKey(team) === teamKey(slug));
}

export function getPlayLine(side: Side, market: string): number | null {
  return side.line === 0 && !HANDICAP_MARKET.test(market) ? null : side.line;
}

export function getPlayEntityName(opp: MarketOpportunity, side: Side): string | null {
  if (side.side === "home" || side.side === "away") return side.name;
  if (opp.entity_type === "player") return opp.entity_name ?? null;
  if (opp.entity_type === "team") return getTeamRowName(opp) ?? null;
  return null;
}

export function getPlayMedia(opp: MarketOpportunity): PlayMedia | null {
  const side = opp.market.sides[0]?.side;
  if (opp.entity_type === "player") return opp.image_url ? { kind: "player", name: opp.entity_name ?? "", url: opp.image_url } : null;
  if (opp.image_url) return { kind: "team", name: getTeamRowName(opp) ?? "", url: opp.image_url };
  const home = opp.home_logo_url ?? null;
  const away = opp.away_logo_url ?? null;
  if (side === "home" && home) return { kind: "team", name: opp.home_team, url: home };
  if (side === "away" && away) return { kind: "team", name: opp.away_team, url: away };
  if (home || away) return { kind: "matchup", away: opp.away_team, home: opp.home_team, awayLogoUrl: away, homeLogoUrl: home };
  return null;
}

export interface DepthLevel {
  book: string;
  odds: number;
  liquidity: number;
}

export interface PlayPrice {
  odds: number;
  decimal: number;
}

export interface PeerRow {
  book: string;
  side1Price?: PlayPrice;
  side1Edge?: number;
  side2Odds?: number;
  side3Odds?: number;
  side1BetLink?: string;
  side2BetLink?: string;
  side3BetLink?: string;
  side1SelectionId?: string;
  side2SelectionId?: string;
  side1FeeAdjusted?: boolean;
  side2FeeAdjusted?: boolean;
  side3FeeAdjusted?: boolean;
  bettable: boolean;
}


export const EXCHANGE_BOOKS = new Set(["novig", "prophetx", "fourcx", "fourcasters", "sxbet", "kalshi", "polymarket"]);

export function getFeeNote(book: string, feeAdjusted: boolean | undefined): string | undefined {
  return feeAdjusted ? `Price includes ${getBookDisplayName(book)} fees` : undefined;
}

export const DFS_BOOKS = new Set([
  "prizepicks",
  "underdog",
  "betr",
  "betrsportsbook",
  "dkpick6",
  "parlayplay",
  "sleeper",
  "dabble",
  "boomsports",
  "chalkboardpicks",
  "smacktok",
]);

export function getWhaleDemandFromOpposite(oppositeBooks: Record<string, BookData>): number {
  let total = 0;
  for (const [book, data] of Object.entries(oppositeBooks)) {
    if (!EXCHANGE_BOOKS.has(book.toLowerCase())) continue;
    if (data.order_book?.length) {
      for (const level of data.order_book) {
        if (level.liquidity > 0 && level.odds > 1) {
          total += level.liquidity * (level.odds - 1);
        }
      }
    } else if ((data.total_liquidity ?? 0) > 0 && data.odds_decimal > 1) {
      total += data.total_liquidity! * (data.odds_decimal - 1);
    }
  }
  return total;
}

export const FAIR_VALUE_WINDOW = 0.015;


export type DepthHighlightMode = "positive" | "negative" | "none";
export type DepthBarVariant = "desktop" | "mobile";

export interface DepthBarStyleTokens {
  gradient: string;
  containerClass: string;
  glowClass: string;
}

const DEPTH_BAR_FILLS: Record<DepthHighlightMode, string> = {
  positive: "color-mix(in srgb, var(--color-accent-value) 90%, transparent)",
  negative: "hsl(var(--primary))",
  none: "hsl(var(--muted-foreground) / 0.4)",
};

export const DEPTH_BAR_STYLES: Record<
  DepthBarVariant,
  Record<DepthHighlightMode, DepthBarStyleTokens>
> = {
  desktop: {
    positive: { gradient: DEPTH_BAR_FILLS.positive, containerClass: "bg-accent-value/10 ring-1 ring-inset ring-accent-value/15", glowClass: "" },
    negative: { gradient: DEPTH_BAR_FILLS.negative, containerClass: "bg-primary/10 ring-1 ring-inset ring-primary/15", glowClass: "" },
    none: { gradient: DEPTH_BAR_FILLS.none, containerClass: "bg-white/[0.04] ring-1 ring-inset ring-white/[0.08]", glowClass: "" },
  },
  mobile: {
    positive: { gradient: DEPTH_BAR_FILLS.positive, containerClass: "bg-accent-value/10 ring-1 ring-inset ring-accent-value/15", glowClass: "" },
    negative: { gradient: DEPTH_BAR_FILLS.negative, containerClass: "bg-primary/10 ring-1 ring-inset ring-primary/15", glowClass: "" },
    none: { gradient: DEPTH_BAR_FILLS.none, containerClass: "bg-white/[0.04]", glowClass: "" },
  },
} as const;

export function formatSportLabel(sport: string): string {
  return sport.toLowerCase() === "mma" ? "MMA" : sport.charAt(0).toUpperCase() + sport.slice(1);
}

export function formatSportAndLeague(sport: string, league: string): string {
  const leagueLabel = league.toUpperCase();
  if (!sport) return leagueLabel;
  const sportLabel = formatSportLabel(sport);
  if (!leagueLabel || sport.toUpperCase() === leagueLabel) return sportLabel;
  return `${sportLabel} \u2022 ${leagueLabel}`;
}

export function formatClassicSportAndLeague(sport: string, league: string): string {
  const leagueLabel = league.toUpperCase();
  if (!sport) return leagueLabel;
  if (!leagueLabel) return sport.charAt(0).toUpperCase() + sport.slice(1);
  if (sport.toUpperCase() === leagueLabel) return leagueLabel;
  return `${sport.charAt(0).toUpperCase() + sport.slice(1)} \u2022 ${leagueLabel}`;
}

export const MIN_WHALE_SIGNAL = 50;
export const MIN_PLAY_SIZE = 100;
export const PLAY_SIZE_MAX = 25000;
export const WHALE_VOLUME_STEP = 50;

export function formatWhaleVolumeFilter(amount: number): string {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(2).replace(/\.?0+$/, "")}k`;
  }
  return `$${amount}`;
}

export const MIN_FAIR_ODDS_LEVEL_LIQUIDITY = 10;

export const BOOK_LOGO_MAP: Record<string, string> = {
  novig: "novig.png",
  pinnacle: "PI.png",
  prophetx: "PX.png",
  draftkings: "DK.png",
  fanduel: "FD.png",
  betmgm: "MG.png",
  caesars: "CS.png",
  fanatics: "FA.png",
  hardrock: "HR.png",
  hardrockbet: "HR.png",
  circa: "Circa.png",
  pinnaclesports: "PI.png",
  rebet: "RB.png",
  betrivers: "KM.png",
  kambi: "KM.png",
  dabble: "DB.png",
  underdog: "UD.png",
  prizepicks: "PP.png",
  parlayplay: "ParlayPlay.png",
  parlay_play: "ParlayPlay.png",
  sleeper: "SLEEPER.png",
  dkpick6: "dkpick6.png",
  polymarket: "PM.png",
  kalshi: "kalshi.png",
  fliff: "FL.png",
  betr: "betr.png",
  draftkingspredictions: "DKP.png",
  betonline: "BO.png",
  bovada: "bovada.png",
  propbuilder: "propbuilder.jpeg",
  betrsportsbook: "betrsportsbook.png",
  shuffle: "shuffle.png",
  sportzino: "sportzino.png",
  sportsbettingag: "sportsbettingag.png",
  tailgate: "tailgate.png",
  boomsports: "boom.png",
  boom: "boom.png",
  boom_fantasy: "boom.png",
};

export const BOOK_CODE_MAP: Record<string, string> = {
  novig: "NOV",
  pinnacle: "PIN",
  prophetx: "PX",
  draftkings: "DK",
  fanduel: "FD",
  betmgm: "MGM",
  caesars: "CZR",
  fanatics: "FAN",
  hardrock: "HR",
  hardrockbet: "HR",
  circa: "CIR",
  rebet: "REB",
  betrivers: "KMB",
  kambi: "KMB",
  dabble: "DAB",
  underdog: "UD",
  prizepicks: "PP",
  parlayplay: "PP",
  sleeper: "SLP",
  polymarket: "POL",
  kalshi: "KAL",
  fliff: "FLF",
  betr: "BTR",
  betonline: "BO",
  bovada: "BV",
  propbuilder: "PRB",
  dkpick6: "DKP6",
  betrsportsbook: "BRS",
  shuffle: "SHF",
  sportzino: "STZ",
  sportsbettingag: "SBAG",
  tailgate: "TG",
  boomsports: "BM",
  boom: "BM",
};


export function formatOdds(odds: number): string {
  return odds > 0 ? `+${odds}` : `${odds}`;
}

export function formatCurrency(amount: number, short = false): string {
  if (short && amount >= 999_950) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (short && amount >= 999.5) {
    return `$${(amount / 1000).toFixed(1)}k`;
  }
  return `$${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function formatBookLiquidity(amount: number | null | undefined): string | null {
  return amount != null && Number.isFinite(amount) && amount >= 0.5 ? formatCurrency(amount, true) : null;
}

function findSideBookData(side: Pick<Side, "books">, book: string): BookData | undefined {
  const key = normalizeWhaleWatchBookKey(book);
  return side.books[book] ?? Object.entries(side.books).find(([name]) => normalizeWhaleWatchBookKey(name) === key)?.[1];
}

export function getReportedBookSize(
  side: Pick<Side, "peers" | "books">,
  book: string,
): number | null {
  const key = normalizeWhaleWatchBookKey(book);
  if (!key) return null;

  const peer = side.peers.find((p) => normalizeWhaleWatchBookKey(p.book) === key);
  const bookData = findSideBookData(side, book);

  let sawZero = false;
  for (const amount of [peer?.limit, bookData?.total_liquidity, bookData?.liquidity]) {
    if (typeof amount === "number" && Number.isFinite(amount) && amount >= 0) {
      if (amount > 0) return amount;
      sawZero = true;
    }
  }
  return sawZero ? 0 : null;
}

export function getBookAvailableLiquidity(
  side: Pick<Side, "peers" | "books">,
  book: string,
): number | null {
  const size = getReportedBookSize(side, book);
  return size != null && size >= 0.5 ? size : null;
}

export function getClassicBookAvailableLiquidity(
  side: Pick<Side, "peers" | "books">,
  book: string,
): number | null {
  const size = getReportedBookSize(side, book);
  return size != null && size > 0 ? size : null;
}

const HANDICAP_MARKET = /spread|handicap|run line|puck line/i;

export function formatSideName(side: Side, market: string): string {
  if (side.side === "draw") {
    return side.name || "Draw";
  }
  if (side.side === "yes" || side.side === "no") {
    return side.side === "yes" ? "Yes" : "No";
  }
  if (market === "total" || market === "totals") {
    const overUnder = side.side === "over" ? "Over" : "Under";
    return `${overUnder} ${Math.abs(side.line)}`;
  }
  if (side.side === "over" || side.side === "under") {
    const nameLower = side.name.toLowerCase();
    if (nameLower.startsWith("over") || nameLower.startsWith("under")) {
      return side.name;
    }
    if (side.line === 0) return `${side.name} ${side.side === "over" ? "Yes" : "No"}`;
    const overUnder = side.side === "over" ? "Over" : "Under";
    return `${side.name} ${overUnder} ${Math.abs(side.line)}`;
  }
  const line = side.line;
  const lineStr = line === 0 ? (HANDICAP_MARKET.test(market) ? " PK" : "") : line > 0 ? ` +${line}` : ` ${line}`;
  return `${side.name}${lineStr}`;
}

export function getShortSideLabel(side: Side, marketName: string): string {
  const full = formatSideName(side, marketName);
  if (side.side === "over" || side.side === "under") {
    if (side.line === 0) return side.side === "over" ? "Yes" : "No";
    return `${side.side === "over" ? "Over" : "Under"} ${Math.abs(side.line)}`;
  }
  return full;
}

export function formatClassicSideName(side: Side, market: string): string {
  if (side.side === "draw") {
    return side.name || "Draw";
  }
  if (side.side === "yes" || side.side === "no") {
    return side.side === "yes" ? "Yes" : "No";
  }
  if (market === "total" || market === "totals") {
    const overUnder = side.side === "over" ? "Over" : "Under";
    return `${overUnder} ${Math.abs(side.line)}`;
  }
  if (side.side === "over" || side.side === "under") {
    const nameLower = side.name.toLowerCase();
    if (nameLower.startsWith("over") || nameLower.startsWith("under")) {
      return side.name;
    }
    const overUnder = side.side === "over" ? "Over" : "Under";
    return `${side.name} ${overUnder} ${Math.abs(side.line)}`;
  }
  const line = side.line;
  const lineStr = line === 0 ? "" : line > 0 ? ` +${line}` : ` ${line}`;
  return `${side.name}${lineStr}`;
}

export function getClassicShortSideLabel(side: Side, marketName: string): string {
  if (side.side === "over" || side.side === "under") {
    return `${side.side === "over" ? "Over" : "Under"} ${Math.abs(side.line)}`;
  }
  return formatClassicSideName(side, marketName);
}

export function capitalizeMarket(market: string): string {
  const marketNames: Record<string, string> = {
    spread: "Point Spread",
    moneyline: "Moneyline",
    total: "Total Points",
    totals: "Total Points",
  };
  return marketNames[market.toLowerCase()] || market;
}

export function formatMarketTitle(marketName: string, marketArity?: number): string {
  const base = capitalizeMarket(marketName);
  return marketArity === 3 && !/3-way/i.test(base) ? `${base} (3-Way)` : base;
}

export function formatClassicMarketTitle(marketName: string, marketArity?: number): string {
  const base = capitalizeMarket(marketName);
  return marketArity === 3 ? `${base} (3-Way)` : base;
}

export function formatPlayName(side: Side, marketName: string, marketArity?: number): string {
  const sideName = formatSideName(side, marketName);
  const marketTitle = formatMarketTitle(marketName, marketArity);
  const isYesNo = side.side === "yes" || side.side === "no" || ((side.side === "over" || side.side === "under") && side.line === 0);
  return isYesNo ? `${marketTitle}: ${sideName}` : `${sideName} ${marketTitle}`;
}

export function formatGameTime(isoString: string, short = false): string {
  const date = new Date(isoString);
  if (short) {
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    const timeStr = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

    if (isToday) return timeStr;
    if (isTomorrow) return `Tmrw ${timeStr}`;
    if (date.getTime() > now.getTime()) {
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfTarget = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const diffDays = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays <= 6) {
        return `${date.toLocaleDateString("en-US", { weekday: "short" })} ${timeStr}`;
      }
    }
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}


export function getWhaleWatchTrackingProps(opp: MarketOpportunity, overrides: {
  sportsbook: string;
  odds_american?: number;
  ev_percentage?: number;
  click_source: "cta" | "odds_badge" | "peer_grid" | "peer_grid_muted" | "empty_state";
}) {
  return {
    sportsbook: overrides.sportsbook,
    page: "whale-watch" as const,
    bet_type: (opp.market_type === "player" || opp.market_type === "player_prop" ? "prop" : "gameline") as "prop" | "gameline",
    odds_american: overrides.odds_american,
    ev_percentage: overrides.ev_percentage,
    league: opp.league,
    click_source: overrides.click_source,
  };
}


export type EdgeTone = "value" | "negative" | "quiet";

export function edgeLabel(edge: number | undefined): { readonly text: string; readonly tone: EdgeTone } {
  if (edge === undefined) return { text: "No edge", tone: "quiet" };
  const shown = Number(edge.toFixed(1));
  if (shown > 0) return { text: `+${shown.toFixed(1)}%`, tone: "value" };
  if (shown < 0) return { text: `${shown.toFixed(1)}%`, tone: "negative" };
  return { text: "0.0%", tone: "quiet" };
}

export function sideSharePct(demand: readonly [number, number], moneyAtRisk: readonly [number | undefined, number | undefined]): number | null {
  const totalDemand = demand[0] + demand[1];
  if (totalDemand > 0) return Math.round((demand[0] / totalDemand) * 100);
  const [money, otherMoney] = moneyAtRisk;
  if (money === undefined || otherMoney === undefined) return null;
  const totalMoney = money + otherMoney;
  return totalMoney > 0 ? Math.round((money / totalMoney) * 100) : 50;
}

export type WhaleVolumeDisplayMode = "edge_supporting" | "same_side_conviction";

export function getWhaleVolumeDisplay(market: MarketGroup): { value: number; mode: WhaleVolumeDisplayMode } {
  const [recommended] = market.sides ?? [];
  const edge = recommended?.edge_percent;

  if (edge !== undefined && edge >= 0) {
    return { value: market.edge_supporting_liquidity ?? 0, mode: "edge_supporting" };
  }

  return { value: market.same_side_conviction_liquidity ?? 0, mode: "same_side_conviction" };
}


export function isValidAmericanOdds(odds: number): boolean {
  return Number.isFinite(odds) && odds !== 0 && odds !== -999;
}

function oddsToImpliedProb(american: number): number {
  if (american > 0) {
    return 100 / (american + 100);
  }
  return Math.abs(american) / (Math.abs(american) + 100);
}

export function impliedProbToAmerican(prob: number): number {
  if (prob <= 0 || prob >= 1) return 0;
  if (prob >= 0.5) {
    return Math.round(-(prob / (1 - prob)) * 100);
  }
  return Math.round(((1 - prob) / prob) * 100);
}

export function oppositeToSelectedOdds(oppDecimalOdds: number, share = 1): number {
  if (oppDecimalOdds <= 1 || share <= 0 || share > 1) return 0;
  const oppImpliedProb = 1 / oppDecimalOdds;
  const selectedImpliedProb = (1 - oppImpliedProb) * share;
  if (selectedImpliedProb <= 0 || selectedImpliedProb >= 1) return 0;
  const selectedDecimalOdds = 1 / selectedImpliedProb;
  return decimalToAmerican(selectedDecimalOdds);
}


export function getBookLogo(bookName: string): string {
  const key = bookName.toLowerCase().replace(/[^a-z0-9]/g, "");
  const filename = BOOK_LOGO_MAP[key] || BOOK_LOGO_MAP[bookName.toLowerCase()];
  if (filename) {
    return sportsbookLogoUrl(filename);
  }
  const canonical = normalizeSportsbookId(bookName);
  if (canonical) return getSportsbookLogoPath(canonical);
  const code = bookName.slice(0, 2).toUpperCase();
  return sportsbookLogoUrl(`${code}.png`);
}

export function getBookCode(bookName: string): string {
  return BOOK_CODE_MAP[bookName.toLowerCase()]
    || getLogoCode(normalizeSportsbookId(bookName))
    || bookName.slice(0, 3).toUpperCase();
}

export function normalizeBookKey(book: string): string {
  return String(book ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function normalizeWhaleWatchBookKey(book: string): string {
  return normalizeSportsbookId(book) || normalizeBookKey(book);
}

const BOOK_DISPLAY_NAMES: Record<string, string> = {
  fanduel: "FanDuel",
  draftkings: "DraftKings",
  betmgm: "BetMGM",
  caesars: "Caesars",
  fanatics: "Fanatics",
  betrivers: "BetRivers",
  hardrock: "Hard Rock",
  hardrockbet: "Hard Rock",
  pinnacle: "Pinnacle",
  novig: "Novig",
  prophetx: "ProphetX",
  circa: "Circa",
  rebet: "ReBet",
  kambi: "BetRivers",
  dabble: "Dabble",
  underdog: "Underdog",
  prizepicks: "PrizePicks",
  parlayplay: "ParlayPlay",
  parlay_play: "ParlayPlay",
  sleeper: "Sleeper",
  dkpick6: "DK Pick6",
  polymarket: "Polymarket",
  kalshi: "Kalshi",
  fliff: "Fliff",
  betr: "Betr",
  draftkingspredictions: "DK Predictions",
  betonline: "BetOnline",
  bovada: "Bovada",
  propbuilder: "PropBuilder",
  betrsportsbook: "Betr Sportsbook",
  shuffle: "Shuffle",
  sportzino: "Sportzino",
  sportsbettingag: "SportsBetting AG",
  tailgate: "Tailgate",
  boomsports: "Boom",
  boom: "Boom",
};

export function getBookDisplayName(book: string): string {
  const canonicalId = normalizeSportsbookId(book);
  if (canonicalId) {
    return sportsbookIdToDisplay(canonicalId);
  }
  const key = normalizeBookKey(book);
  if (BOOK_DISPLAY_NAMES[key]) {
    return BOOK_DISPLAY_NAMES[key];
  }
  return book
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function isBookSelected(book: string, selectedBookKeys: string[]): boolean {
  if (selectedBookKeys.length === 0) return false;
  const key = normalizeWhaleWatchBookKey(book);
  return selectedBookKeys.some(
    (selectedKey) => normalizeWhaleWatchBookKey(selectedKey) === key,
  );
}

type PlaySide = Pick<PeerRow, "side1Price" | "side1Edge" | "bettable">;

export function hasPlaySideEdge(price: PlaySide, fairProbSide1: number): boolean {
  if (!price.bettable) return false;
  if (price.side1Edge !== undefined) return price.side1Edge > 0;
  if (!price.side1Price) return false;
  return edgePercentAt(fairProbSide1, price.side1Price.decimal) > 0;
}

export function isPlaySideCellLit(
  price: PlaySide & Pick<PeerRow, "book">,
  fairProbSide1: number,
  selectedBookKeys: string[],
): boolean {
  const bookAvailable =
    selectedBookKeys.length === 0 || isBookSelected(price.book, selectedBookKeys);
  return bookAvailable && hasPlaySideEdge(price, fairProbSide1);
}

export function getSecondaryBooksForSide(
  peers: ListPeer[],
  selectedBookKeys: string[],
  bestBook: string,
  bestOdds: number,
): string[] {
  const bestKey = normalizeWhaleWatchBookKey(bestBook);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const peer of peers) {
    if (selectedBookKeys.length > 0 && !isBookSelected(peer.book, selectedBookKeys)) continue;
    const key = normalizeWhaleWatchBookKey(peer.book);
    if (key === bestKey || seen.has(key)) continue;
    if (peer.odds_american === bestOdds || (peer.edge_percent ?? 0) > 0) {
      seen.add(key);
      result.push(peer.book);
    }
  }
  return result.sort((a, b) =>
    normalizeWhaleWatchBookKey(a).localeCompare(normalizeWhaleWatchBookKey(b)),
  );
}

export function getClassicSecondaryBooksForSide(
  peers: PeerOdds[],
  selectedBookKeys: string[],
  bestBook: string,
  oppositeSideBooks: Record<string, BookData>,
  share = 1,
  displayedBestOdds?: number,
): string[] {
  const bestKey = normalizeWhaleWatchBookKey(bestBook);
  let bestOdds: number | undefined =
    displayedBestOdds !== undefined && isValidAmericanOdds(displayedBestOdds)
      ? displayedBestOdds
      : undefined;
  if (bestOdds === undefined) {
    for (const peer of peers) {
      if (
        normalizeWhaleWatchBookKey(peer.book) === bestKey &&
        isValidAmericanOdds(peer.odds_american)
      ) {
        if (bestOdds === undefined || peer.odds_american > bestOdds) {
          bestOdds = peer.odds_american;
        }
      }
    }
  }
  const seen = new Set<string>();
  const result: string[] = [];
  for (const peer of peers) {
    if (selectedBookKeys.length > 0 && !isBookSelected(peer.book, selectedBookKeys)) continue;
    if (!isValidAmericanOdds(peer.odds_american)) continue;
    const key = normalizeWhaleWatchBookKey(peer.book);
    if (key === bestKey || seen.has(key)) continue;
    const tiedWithBest = bestOdds !== undefined && peer.odds_american === bestOdds;
    const fairProb = getTopWhaleFairProb(withoutPlayBook(oppositeSideBooks, peer.book), share);
    const positiveEdge = edgePercentAt(fairProb, peer.odds_decimal) > 0;
    if (tiedWithBest || positiveEdge) {
      seen.add(key);
      result.push(peer.book);
    }
  }
  return result.sort((a, b) =>
    normalizeWhaleWatchBookKey(a).localeCompare(normalizeWhaleWatchBookKey(b)),
  );
}

function withoutPlayBook(books: Record<string, BookData>, playBook: string): Record<string, BookData> {
  const play = normalizeWhaleWatchBookKey(playBook);
  return Object.fromEntries(Object.entries(books).filter(([book]) => normalizeWhaleWatchBookKey(book) !== play));
}

function selectedSideProb(oppositeDecimal: number, share: number): number {
  if (oppositeDecimal <= 1 || share <= 0 || share > 1) return 0;
  const prob = (1 - 1 / oppositeDecimal) * share;
  return prob > 0 && prob < 1 ? prob : 0;
}

function getTopWhaleFairProb(oppositeSideBooks: Record<string, BookData>, share = 1): number {
  let best = 0;
  for (const [book, data] of Object.entries(oppositeSideBooks)) {
    if (!EXCHANGE_BOOKS.has(book.toLowerCase())) continue;
    if (data.order_book && data.order_book.length > 0) {
      for (const level of data.order_book) {
        if (level.liquidity >= MIN_FAIR_ODDS_LEVEL_LIQUIDITY) best = Math.max(best, selectedSideProb(level.odds, share));
      }
    } else if (data.liquidity > 0) {
      best = Math.max(best, selectedSideProb(data.odds_decimal, share));
    }
  }
  return best;
}

function edgePercentAt(fairProb: number, retailDecimal: number): number {
  return fairProb > 0 && fairProb < 1 && retailDecimal > 1 ? (fairProb * retailDecimal - 1) * 100 : 0;
}

export function getEdgeSupportingLiquidity(
  oppositeSideBooks: Record<string, BookData>,
  retailOdds: number,
  share = 1
): number {
  if (!isValidAmericanOdds(retailOdds)) return 0;

  const retailBreakEven = oddsToImpliedProb(retailOdds);
  let total = 0;

  for (const [book, data] of Object.entries(oppositeSideBooks)) {
    if (!EXCHANGE_BOOKS.has(book.toLowerCase())) continue;

    if (data.order_book && data.order_book.length > 0) {
      for (const level of data.order_book) {
        if (level.liquidity > 0 && level.odds > 1) {
          const displayOdds = oppositeToSelectedOdds(level.odds, share);
          if (displayOdds === 0) continue;

          const sharpImpliedProb = oddsToImpliedProb(displayOdds);
          if (sharpImpliedProb >= retailBreakEven) {
            const actualMoney = level.liquidity * (level.odds - 1);
            total += actualMoney;
          }
        }
      }
    } else if (data.liquidity && data.liquidity > 0 && data.odds_decimal > 1) {
      const displayOdds = oppositeToSelectedOdds(data.odds_decimal, share);
      if (displayOdds !== 0) {
        const sharpImpliedProb = oddsToImpliedProb(displayOdds);
        if (sharpImpliedProb >= retailBreakEven) {
          const actualMoney = data.liquidity * (data.odds_decimal - 1);
          total += actualMoney;
        }
      }
    }
  }

  return Math.round(total * 100) / 100;
}

export function recomputeEdgePercent(
  oppositeSideBooks: Record<string, BookData>,
  retailOddsAmerican: number,
  share = 1
): number {
  if (!isValidAmericanOdds(retailOddsAmerican)) return 0;
  return edgePercentAt(getTopWhaleFairProb(oppositeSideBooks, share), americanToDecimal(retailOddsAmerican));
}


export function buildDepthLevelsForSelectedSide(
  oppositeSide: Side,
  playBook: string,
  maxLevels = 10,
  share = 1
): DepthLevel[] {
  const depthData: DepthLevel[] = [];
  const seen = new Set<string>();
  const play = normalizeWhaleWatchBookKey(playBook);

  for (const [book, data] of Object.entries(oppositeSide.books)) {
    if (!EXCHANGE_BOOKS.has(book.toLowerCase()) || normalizeWhaleWatchBookKey(book) === play) continue;

    if (data.order_book && data.order_book.length > 0) {
      for (const level of data.order_book) {
        if (level.liquidity > 0 && level.odds > 1) {
          const displayOdds = oppositeToSelectedOdds(level.odds, share);
          if (displayOdds === 0) continue;

          const key = `${book}:${displayOdds}`;
          if (seen.has(key)) continue;
          seen.add(key);

          const actualMoney = level.liquidity * (level.odds - 1);
          depthData.push({
            book,
            odds: displayOdds,
            liquidity: Math.round(actualMoney * 100) / 100,
          });
        }
      }
    } else if (data.liquidity && data.liquidity > 0 && data.odds_decimal > 1) {
      const displayOdds = oppositeToSelectedOdds(data.odds_decimal, share);
      if (displayOdds !== 0) {
        const key = `${book}:${displayOdds}`;
        if (!seen.has(key)) {
          seen.add(key);
          const actualMoney = data.liquidity * (data.odds_decimal - 1);
          depthData.push({
            book,
            odds: displayOdds,
            liquidity: Math.round(actualMoney * 100) / 100,
          });
        }
      }
    }
  }

  depthData.sort((a, b) => oddsToImpliedProb(b.odds) - oddsToImpliedProb(a.odds));
  return depthData.slice(0, maxLevels);
}

export function buildPeerRows(side1: Side<PeerOdds>, side2: Side<PeerOdds>, isHere: (book: string) => boolean, maxRows = 10, side3?: Side<PeerOdds>): PeerRow[] {
  const peer1ByBook = new Map(side1.peers.map((p) => [p.book, p]));
  const peer2ByBook = new Map(side2.peers.map((p) => [p.book, p]));
  const peer3ByBook = new Map((side3?.peers ?? []).map((p) => [p.book, p]));
  const allBooks = new Set([
    ...peer1ByBook.keys(),
    ...peer2ByBook.keys(),
    ...peer3ByBook.keys(),
    ...Object.keys(side1.books ?? {}),
    ...Object.keys(side2.books ?? {}),
    ...Object.keys(side3?.books ?? {}),
  ]);

  const peerRows: PeerRow[] = [];
  for (const book of allBooks) {
    const p1 = peer1ByBook.get(book);
    const p2 = peer2ByBook.get(book);
    const p3 = peer3ByBook.get(book);
    const b1 = side1.books?.[book];
    const b2 = side2.books?.[book];
    const b3 = side3?.books?.[book];

    const side1Price: PlayPrice | undefined = p1
      ? { odds: p1.odds_american, decimal: p1.odds_decimal }
      : b1 && isValidAmericanOdds(b1.odds_american)
        ? { odds: b1.odds_american, decimal: b1.odds_decimal }
        : undefined;
    const side2Odds = p2?.odds_american ?? (isValidAmericanOdds(b2?.odds_american ?? 0) ? b2?.odds_american : undefined);
    const side3Odds = p3?.odds_american ?? (isValidAmericanOdds(b3?.odds_american ?? 0) ? b3?.odds_american : undefined);

    const bettable = isHere(book);
    const side1BetLink = bettable ? p1?.bet_link || side1.books[book]?.bet_link : undefined;
    const side2BetLink = bettable ? p2?.bet_link || side2.books[book]?.bet_link : undefined;
    const side3BetLink = bettable ? p3?.bet_link || side3?.books?.[book]?.bet_link : undefined;

    if (side1Price === undefined && side2Odds === undefined && side3Odds === undefined) {
      continue;
    }

    peerRows.push({
      book,
      side1Price,
      side1Edge: p1?.edge_percent,
      side2Odds,
      side3Odds,
      side1BetLink,
      side2BetLink,
      side3BetLink,
      side1SelectionId: p1?.selection_id ?? side1.books?.[book]?.selection_id,
      side2SelectionId: p2?.selection_id ?? side2.books?.[book]?.selection_id,
      side1FeeAdjusted: Boolean(p1?.fee_adjusted || b1?.fee_adjusted),
      side2FeeAdjusted: Boolean(p2?.fee_adjusted || b2?.fee_adjusted),
      side3FeeAdjusted: Boolean(p3?.fee_adjusted || b3?.fee_adjusted),
      bettable,
    });
  }

  peerRows.sort((a, b) => (b.side1Price?.odds || -999) - (a.side1Price?.odds || -999));
  return peerRows.slice(0, maxRows);
}

export function getSideBookSelectionId(side: Side<PeerOdds>, book: string): string | undefined {
  return side.peers.find((p) => p.book === book)?.selection_id ?? side.books?.[book]?.selection_id;
}

function sideCapacity(side: Side, book: string): OddsHistoryQuoteCapacity | undefined {
  const candidates: Array<[OddsHistoryQuoteCapacity["kind"], number | undefined]> = [
    ["limit", side.peers.find((p) => p.book === book)?.limit],
    ["liquidity", side.books?.[book]?.total_liquidity],
    ["liquidity", side.books?.[book]?.liquidity],
  ];
  for (const [kind, amount] of candidates) {
    if (amount != null && Number.isFinite(amount) && amount > 0) return { kind, amount };
  }
  return undefined;
}

export function historyOpposite(
  side2: Side<PeerOdds>,
  market: MarketGroup<PeerOdds>,
  peerRows: readonly PeerRow[],
  book: string,
): OddsHistoryOpposite | undefined {
  if (market.third_side) return undefined;
  const selectionId = getSideBookSelectionId(side2, book);
  const bettableOdds = peerRows.flatMap((row) => (row.bettable && row.side2Odds != null ? [row.side2Odds] : []));
  const bestOdds = peerRows.some((row) => row.book === side2.best_book && !row.bettable) ? Math.max(...bettableOdds) : side2.best_odds;
  const quoteOf = (row: PeerRow): OddsHistoryQuoteContext => ({
    oddsAmerican: row.side2Odds != null && Number.isFinite(row.side2Odds) ? row.side2Odds : undefined,
    betLinks: toBetLinkSet(row.side2BetLink, undefined, undefined) ?? undefined,
    capacity: sideCapacity(side2, row.book),
    isBestPrice: row.bettable && row.side2Odds === bestOdds ? true : undefined,
  });
  const anchorRow = peerRows.find((row) => row.book === book);
  const peers: OddsHistoryPeerBook[] = peerRows
    .filter((row) => row.side2Odds != null && (row.book !== book || selectionId == null))
    .map((row) => ({
      book: row.book,
      bookDisplayName: getBookDisplayName(row.book),
      ...(row.side2SelectionId ? { selectionId: row.side2SelectionId } : {}),
      ...quoteOf(row),
      line: getPlayLine(side2, market.market),
      oddsAmerican: row.side2Odds as number,
    }));
  return {
    selectionId,
    side: side2.side,
    line: getPlayLine(side2, market.market),
    context: anchorRow && selectionId ? { quote: quoteOf(anchorRow) } : undefined,
    peers,
  };
}

export function getFairProbabilityForSide1(side2: Side, playBook: string, share = 1): number {
  return getTopWhaleFairProb(withoutPlayBook(side2.books, playBook), share);
}

export function computeDepthStats(depthLevels: DepthLevel[]): {
  maxLiquidity: number;
  totalLiquidity: number;
  avgOdds: number;
} {
  const maxLiquidity = Math.max(...depthLevels.map((d) => d.liquidity), 1);
  const totalLiquidity = depthLevels.reduce((sum, d) => sum + d.liquidity, 0);
  const avgOdds =
    depthLevels.length > 0 && totalLiquidity > 0
      ? impliedProbToAmerican(
        depthLevels.reduce(
          (sum, d) => sum + oddsToImpliedProb(d.odds) * d.liquidity,
          0
        ) / totalLiquidity
      )
      : 0;
  return { maxLiquidity, totalLiquidity, avgOdds };
}

export function computeDepthLevelHighlighting(args: {
  depthLevels: DepthLevel[];
  edgePercent: number | undefined;
  retailOddsAmerican: number;
  oppositeSideBooks: Record<string, BookData>;
  playBook: string;
  fairValueWindow?: number;
  fairOutcomeShare?: number;
}): {
  highlightModes: DepthHighlightMode[];
  thresholdIndex: number | null;
} {
  const {
    depthLevels,
    edgePercent,
    retailOddsAmerican,
    oppositeSideBooks,
    playBook,
    fairValueWindow = FAIR_VALUE_WINDOW,
    fairOutcomeShare = 1,
  } = args;

  if (edgePercent === undefined || !isValidAmericanOdds(retailOddsAmerican)) {
    return { highlightModes: depthLevels.map(() => "none"), thresholdIndex: null };
  }

  const isPositiveEdge = edgePercent > 0;

  const retailBreakEven = oddsToImpliedProb(retailOddsAmerican);

  const fairValueProb = getTopWhaleFairProb(withoutPlayBook(oppositeSideBooks, playBook), fairOutcomeShare);

  const highlightModes: DepthHighlightMode[] = [];
  let thresholdIndex: number | null = null;

  for (let i = 0; i < depthLevels.length; i++) {
    const sharpImpliedProb = oddsToImpliedProb(depthLevels[i].odds);
    let mode: DepthHighlightMode = "none";

    if (isPositiveEdge) {
      if (sharpImpliedProb >= retailBreakEven) {
        mode = "positive";
      }
    } else if (fairValueProb > 0 && Math.abs(sharpImpliedProb - fairValueProb) <= fairValueWindow) {
      mode = "negative";
    }

    if (isPositiveEdge && thresholdIndex === null && mode === "none") {
      thresholdIndex = i;
    }

    highlightModes.push(mode);
  }

  return { highlightModes, thresholdIndex };
}
